import { CustodiansField } from 'components/secondaryMarket/shared';
import { FormGroupValidationFeedbackMessage } from 'components/secondaryMarket/shared/FormGroupValidationFeedbackMessage';
import { LoadingError } from 'components/secondaryMarket/shared/LoadingError';
import { VerticalFormGroup } from 'components/secondaryMarket/shared/VerticalFormGroup';
import { AmountWithUnit } from 'components/shared/Amount/Amount';
import { getCurrencySymbol } from 'components/shared/NewAmountInput/CurrencySymbol';
import { FC, Fragment } from 'react';
import { useIntl } from 'react-intl';

import type { DealSplitProps } from './DealSplit.connect';
import { connectDealSlit as connectDealSplit } from './DealSplit.connect';

const DealSplit: FC<DealSplitProps> = ({
  error,
  custodians,
  currency,
  onRowCustodianChanged,
  onRowNotionalChanged,
  onRetryLoadingCustodians,
  dealSplitRows,
  onAddNewRow,
  onRemoveRow,
  hasMultipleCustodians,
  frozen,
}) => {
  const { formatMessage } = useIntl();
  const currencySymbol = getCurrencySymbol(currency);
  if (error) {
    return (
      <VerticalFormGroup inputId="custodian" label="custodian" className="col-12">
        <LoadingError
          onRetry={onRetryLoadingCustodians}
          severity={error !== 'not_found' ? 'danger' : 'warning'}
          labelKey={
            error !== 'not_found'
              ? 'secondaryMarket.errors.clientLoadFailed'
              : 'secondaryMarket.errors.custodiansNotFound'
          }
        />
      </VerticalFormGroup>
    );
  }
  return (
    <>
      <VerticalFormGroup inputId="custodian" label="custodian" className="col-md-6">
        <div className="d-flex flex-column">
          {dealSplitRows.map(({ custodianId }, rowIndex) => (
            <Fragment key={custodianId}>
              <CustodiansField
                className="mt-1"
                selectedBdrId={custodianId}
                custodians={custodians}
                onChange={value => onRowCustodianChanged(rowIndex, value)}
                disabled={frozen}
                readonly={!hasMultipleCustodians}
              />
              <FormGroupValidationFeedbackMessage inputId={`custodian-${rowIndex}`} />
            </Fragment>
          ))}
          <div className="d-flex align-items-end mt-3">
            {hasMultipleCustodians && (
              <button
                type="button"
                className="btn btn-flat-primary btn-icon-start"
                onClick={onAddNewRow}
                disabled={frozen}
              >
                <em className="icon">add</em>
                {formatMessage({ id: 'secondaryMarket.details.addCustodian' })}
              </button>
            )}
          </div>
        </div>
      </VerticalFormGroup>
      <VerticalFormGroup inputId="allocated-notional" label="notional" className="col-md-6">
        <div className="d-flex flex-column">
          {dealSplitRows.map(({ notional, custodianId }, rowIndex) => (
            <div className="d-flex align-items-end mt-1" key={`${custodianId}-${rowIndex}`}>
              <AmountWithUnit
                key={custodianId}
                value={notional}
                unit={currencySymbol}
                className="flex-grow-1"
                e2e="Notional"
                onChange={notional => onRowNotionalChanged(rowIndex, notional!)}
                incrementStep={1}
                isReadOnly={!hasMultipleCustodians}
                isDisabled={frozen}
              />
              {hasMultipleCustodians && (
                <button
                  type="button"
                  className="btn ms-1 btn-flat-secondary"
                  onClick={() => onRemoveRow(rowIndex)}
                  disabled={frozen}
                >
                  <em className="icon">delete</em>
                </button>
              )}
            </div>
          ))}
        </div>
      </VerticalFormGroup>
    </>
  );
};

export const ConnectedDealSplit = connectDealSplit(DealSplit);
